import { logout } from '@/features/auth';
import type { State } from '@/reducers/types';
import { ReportCount } from '@/types';
import { createSlice } from '@reduxjs/toolkit';
import { getReportsCount } from './actions';

export interface ReportsCountState {
  reportsCounts: ReportCount[] | null;
  pagination: {
    hasNextPage: boolean;
    currentPage: number;
  };
  limit: number;
  sortBy: 'email' | 'count';
  sortOrder: 'asc' | 'desc';
  searchQuery: string;
}

const initialState: ReportsCountState = {
  reportsCounts: null,
  pagination: {
    hasNextPage: false,
    currentPage: 1,
  },
  limit: 10,
  sortBy: 'email',
  sortOrder: 'desc',
  searchQuery: '',
};

export const { name, reducer, actions } = createSlice({
  name: 'reportsCount',
  initialState,
  reducers: {
    setLimit: (state, action) => {
      state.limit = action.payload;
      state.reportsCounts = null;
      state.pagination = {
        hasNextPage: false,
        currentPage: 1,
      };
    },
    setSortBy: (state, action: { payload: 'email' | 'count' }) => {
      state.sortBy = action.payload;
      state.reportsCounts = null;
      state.pagination = {
        hasNextPage: false,
        currentPage: 1,
      };
    },
    setSortOrder: (state, action: { payload: 'asc' | 'desc' }) => {
      state.sortOrder = action.payload;
      state.reportsCounts = null;
      state.pagination = {
        hasNextPage: false,
        currentPage: 1,
      };
    },
    setSearchQuery: (state, action: { payload: string }) => {
      state.searchQuery = action.payload;
      state.reportsCounts = null;
      state.pagination = {
        hasNextPage: false,
        currentPage: 1,
      };
    },
    setCurrentPage: (state, action) => {
      state.pagination.currentPage = action.payload;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(getReportsCount.fulfilled, (state, action) => {
        state.reportsCounts = action.payload.data;
        state.pagination.hasNextPage = !action.payload.isLast;
      })
      .addCase(logout.fulfilled, () => initialState);
  },
});

export const { setLimit, setSortBy, setSortOrder, setCurrentPage, setSearchQuery } = actions;

export const selectReportsCount = (state: State) => state[name].reportsCounts;
export const selectPagination = (state: State) => state[name].pagination;
export const selectLimit = (state: State) => state[name].limit;
export const selectSortBy = (state: State) => state[name].sortBy;
export const selectSortOrder = (state: State) => state[name].sortOrder;
export const selectSearchQuery = (state: State) => state[name].searchQuery;
